import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Check, Loader2, X, Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Newsletter Signup Component
 * Collects email addresses for travel alerts
 */
const NewsletterSignup = ({ variant = 'inline', className = '' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        const data = await response.json();
        setStatus('error');
        setErrorMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Unable to subscribe. Please try again later.');
    }
  };

  const resetForm = () => {
    setStatus('idle');
    setErrorMessage('');
  };

  // Inline variant - compact horizontal form
  if (variant === 'inline') {
    return (
      <div className={`${className}`}>
        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg"
            >
              <Check className="w-5 h-5" />
              <span className="text-sm font-medium">You're subscribed! Check your inbox.</span>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-2"
            >
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === 'error') resetForm();
                  }}
                  placeholder="Enter your email for travel alerts"
                  className={`w-full h-10 pl-10 pr-4 rounded-lg border ${
                    status === 'error' 
                      ? 'border-red-300 focus:ring-red-200' 
                      : 'border-slate-200 focus:ring-blue-200'
                  } focus:outline-none focus:ring-2 text-sm`}
                  disabled={status === 'loading'}
                />
              </div>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="h-10 px-4 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 min-w-[100px]"
              >
                {status === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    Subscribe
                  </>
                )}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
        {status === 'error' && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-500 text-xs mt-1 flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            {errorMessage}
          </motion.p>
        )}
      </div>
    );
  }

  // Card variant - full card with description
  return (
    <div className={`bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 ${className}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-800">Get Travel Alerts</h3>
          <p className="text-sm text-slate-600">
            Subscribe for tips on the best times to fly and airport updates.
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 text-green-600 bg-green-100 px-4 py-3 rounded-lg"
          >
            <Check className="w-5 h-5" />
            <span className="text-sm font-medium">You're subscribed! Check your inbox.</span>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === 'error') resetForm();
                }}
                placeholder="your@email.com"
                className={`w-full h-11 pl-10 pr-4 rounded-lg border ${
                  status === 'error' 
                    ? 'border-red-300 focus:ring-red-200' 
                    : 'border-slate-200 focus:ring-blue-200'
                } focus:outline-none focus:ring-2 bg-white`}
                disabled={status === 'loading'}
              />
            </div>
            {status === 'error' && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-xs flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                {errorMessage}
              </motion.p>
            )}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full h-11 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Subscribe to Alerts
                </>
              )}
            </button>
            <p className="text-xs text-slate-500 text-center">
              No spam. Unsubscribe anytime.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsletterSignup;
